import React from "react";

import EcoIcon from "../../../components/eco-icon/EcoIcon";

import "./QuestionField.css";

export type QuestionFieldType = "BOOLEAN" | "SCALE" | "TEXT" | "DATE" | "SINGLE_CHOICE" | "MULTI_CHOICE";

export type QuestionFieldQuestion = {
  id: string;
  type: QuestionFieldType | string;
  prompt: string;
  required?: boolean;
  minValue?: number | null;
  maxValue?: number | null;
  options?: Array<{ id: string; label: string }>;
};

export type QuestionFieldValue = boolean | number | string | string[] | null;

type Props = {
  question: QuestionFieldQuestion;
  /** Número mostrado ao lado do enunciado. */
  index: number;
  value: QuestionFieldValue;
  onChange: (value: QuestionFieldValue) => void;
  /** Obrigatória sem resposta depois de tentar avançar: destaca o cartão. */
  missing?: boolean;
  disabled?: boolean;
};

/** Escala com até 11 valores vira botões; maior que isso, campo numérico. */
const MAX_SCALE_BUTTONS = 11;

/**
 * Uma pergunta de formulário respondida pelo polegar (redesign de 15/10/2026):
 * Sim/Não em dois botões, escala em botões, escolhas em linhas tocáveis.
 * Usado na auditoria de loja e na avaliação de freelancer.
 */
const QuestionField: React.FC<Props> = ({ question, index, value, onChange, missing, disabled }) => {
  const labelId = `qf-${question.id}`;
  const min = question.minValue ?? 0;
  const max = question.maxValue ?? 10;
  const scaleValues = max - min + 1 <= MAX_SCALE_BUTTONS ? Array.from({ length: max - min + 1 }, (_, i) => min + i) : null;

  const heading = (
    <span className="qField__prompt" id={labelId}>
      <span className="qField__num">{index}</span>
      <span>{question.prompt}</span>
    </span>
  );

  const body = (() => {
    switch (question.type) {
      case "BOOLEAN":
        return (
          <div className="qField__pair" role="radiogroup" aria-labelledby={labelId}>
            <button
              type="button"
              role="radio"
              aria-checked={value === true}
              className={`qField__choice${value === true ? " qField__choice--yes" : ""}`}
              onClick={() => onChange(value === true ? null : true)}
              disabled={disabled}
            >
              {value === true ? <EcoIcon name="check" size={18} strokeWidth={3} /> : null}
              Sim
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={value === false}
              className={`qField__choice${value === false ? " qField__choice--no" : ""}`}
              onClick={() => onChange(value === false ? null : false)}
              disabled={disabled}
            >
              {value === false ? <EcoIcon name="x" size={18} strokeWidth={3} /> : null}
              Não
            </button>
          </div>
        );

      case "SCALE":
        return scaleValues ? (
          <>
            <div
              className="qField__scale"
              role="radiogroup"
              aria-labelledby={labelId}
              style={{ gridTemplateColumns: `repeat(${Math.min(scaleValues.length, 6)}, minmax(0, 1fr))` }}
            >
              {scaleValues.map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={value === n}
                  className={`qField__choice${value === n ? " qField__choice--scale" : ""}`}
                  onClick={() => onChange(value === n ? null : n)}
                  disabled={disabled}
                >
                  {n}
                </button>
              ))}
            </div>
            <span className="qField__scaleHint">
              <span>{min}</span>
              <span>{max}</span>
            </span>
          </>
        ) : (
          <input
            type="number"
            className="qField__input"
            aria-labelledby={labelId}
            min={min}
            max={max}
            placeholder={`${min} a ${max}`}
            value={typeof value === "number" ? value : ""}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
            disabled={disabled}
          />
        );

      case "TEXT":
        return (
          <textarea
            className="qField__input qField__textarea"
            aria-labelledby={labelId}
            rows={3}
            placeholder="Escreva aqui..."
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
        );

      case "DATE":
        return (
          <input
            type="date"
            className="qField__input"
            aria-labelledby={labelId}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value || null)}
            disabled={disabled}
          />
        );

      case "SINGLE_CHOICE":
        return (
          <div className="qField__options" role="radiogroup" aria-labelledby={labelId}>
            {(question.options ?? []).map((o) => (
              <label key={o.id} className={`qField__option${value === o.id ? " qField__option--checked" : ""}`}>
                <input
                  type="radio"
                  name={`qf-radio-${question.id}`}
                  checked={value === o.id}
                  onChange={() => onChange(o.id)}
                  disabled={disabled}
                />
                {o.label}
              </label>
            ))}
            {typeof value === "string" && value ? (
              <button type="button" className="qField__clear" onClick={() => onChange(null)} disabled={disabled}>
                Limpar escolha
              </button>
            ) : null}
          </div>
        );

      case "MULTI_CHOICE": {
        const current = Array.isArray(value) ? value : [];
        return (
          <div className="qField__options" role="group" aria-labelledby={labelId}>
            <span className="qField__hint">Marque todas que se aplicam</span>
            {(question.options ?? []).map((o) => {
              const checked = current.includes(o.id);
              return (
                <label key={o.id} className={`qField__option${checked ? " qField__option--checked" : ""}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onChange(checked ? current.filter((id) => id !== o.id) : [...current, o.id])}
                    disabled={disabled}
                  />
                  {o.label}
                </label>
              );
            })}
          </div>
        );
      }

      default:
        return <span className="qField__hint">Tipo de pergunta não suportado aqui.</span>;
    }
  })();

  return (
    <section className={`qField${missing ? " qField--missing" : ""}`}>
      <div className="qField__head">
        {heading}
        {missing ? <span className="qField__badge">Obrigatória</span> : null}
      </div>
      {body}
    </section>
  );
};

/** Pergunta tem resposta? (mesma regra da validação das telas). */
export function hasAnswer(type: string, value: QuestionFieldValue): boolean {
  if (type === "BOOLEAN") return value === true || value === false;
  if (type === "SCALE") return typeof value === "number";
  if (type === "TEXT") return typeof value === "string" && value.trim().length > 0;
  if (type === "DATE" || type === "SINGLE_CHOICE") return typeof value === "string" && value.length > 0;
  if (type === "MULTI_CHOICE") return Array.isArray(value) && value.length > 0;
  return false;
}

export default QuestionField;
